import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Upload,
  Alert,
  Image,
  Popconfirm,
  Divider,
  Space,
  Row,
  Switch,
} from "antd";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { countryCodeOptions } from "@shared/lib/constants";
import {
  GenderEnum,
  ICreateInfluencerRequest,
  IInfluencer,
  INFLUENCER_ACCOUNT_STATUS,
  InfluencerType,
} from "@shared/lib/types/influencer";
import styles from "./styles.module.scss";
import { PlusOutlined } from "@ant-design/icons";
import { useGetLocationFacets } from "../lib/hooks/useGetLocationFacets";
import { CircularProgress } from "@shared/ui";
import { useGetAgencies } from "@entities/agency";
import { ACCOUNT_STATUS } from "@shared/lib/types";

const { Option } = Select;
interface Props {
  onSubmit: (values: Partial<IInfluencer>) => void;
  isSubmitting: boolean;
  initialCategories: string[];
  initialValues?: IInfluencer;
}

export const InfluencerUpdateForm = ({
  onSubmit,
  isSubmitting,
  initialCategories,
  initialValues,
}: Props) => {
  const [profilePicUrl, setProfilePicUrl] = useState(
    initialValues?.profilePicUrl
  );
  const [isUploading, setIsUploading] = useState(false);
  const [form] = Form.useForm();
  const [newRole, setNewRole] = useState("");

  const [selectedLocation, setSelectedLocation] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
  >({
    label: initialValues?.locationObj?.description || "",
    value: initialValues?.locationObj?.locationId || "",
  });
  // const [selectedAgency, setselectedAgency] = useState<
  //   | {
  //       label: string;
  //       value: string;
  //     }
  //   | undefined
  // >({
  //   label: initialValues?.agency?.name || "",
  //   value: initialValues?.agency?._id || "",
  // });

  const [searchLocation, setSearchLocation] = useState<string>("");
  // const [searchAgency, setSearchAgency] = useState<string>("");
  // const [accountStatus, setAccountStatus] = useState(
  //   initialValues?.accountStatus
  // );

  const { locationFacets, isFetching } = useGetLocationFacets(searchLocation);
  // const { agencies, isFetching: isFetchingAgencies } =
  //   useGetAgencies(searchAgency);

  const [availableCategories, setAvailableCategories] =
    useState(initialCategories);

  useEffect(() => {
    setAvailableCategories(initialCategories);
  }, [initialCategories]);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleImageUpload = async (file: File) => {
    if (!file) return;
    const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validImageTypes.includes(file.type)) {
      ToastService.error("Only image files are allowed.");
      return;
    }
    try {
      setIsUploading(true);
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "influencer-profile-pics",
        file
      );
      setProfilePicUrl(uploadedUrl);
      setIsUploading(false);
    } catch (error) {
      setIsUploading(false);
      ToastService.error("Image upload failed. Please try again.");
    }
  };

  const handleDeleteImage = () => {
    setProfilePicUrl("");
    ToastService.success("Profile picture removed successfully.");
  };

  const addNewCategories = (roleName: string) => {
    if (!roleName.trim() || availableCategories.includes(roleName)) {
      return;
    }

    const updatedRoles = [...availableCategories, roleName];
    setAvailableCategories(updatedRoles);
    setNewRole("");
    form.setFieldsValue({
      category: [...(form.getFieldValue("category") || []), roleName],
    });
    setDropdownOpen(false);
  };
  const handleSelectLocation = (value: string) => {
    const selected = (locationFacets || []).find(
      (item) => item.locationId === value
    );
    if (selected) {
      setSelectedLocation({
        label: selected.description,
        value: selected.locationId,
      });
      form.setFieldValue("locationId", value);
    }
  };
  // const handleSelectAgency = (value: string) => {
  //   const selected = (agencies?.data || []).find((item) => item._id === value);
  //   if (selected) {
  //     setselectedAgency({
  //       label: selected.name,
  //       value: selected._id,
  //     });
  //     form.setFieldValue("agencyId", value);
  //   }
  // };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...initialValues,
        locationId: initialValues?.locationObj?.locationId,
      }}
      onFinish={(values) => {
        onSubmit({
          ...values,
          profilePicUrl,
          accountStatus: ACCOUNT_STATUS.ACTIVE,
          phoneNumber: values.phoneNumber?.replace(/\s/g, ""),
        });
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <div className={styles.form}>
        <Form.Item label="Profile Picture">
          <Upload
            showUploadList={false}
            beforeUpload={(file) => handleImageUpload(file)}
          >
            <Button disabled={isUploading}>Upload Profile Picture</Button>
          </Upload>
          {profilePicUrl && (
            <div style={{ marginTop: "16px" }}>
              <Image src={profilePicUrl} alt="Profile Picture" width={100} />
              <Popconfirm
                title="Are you sure to delete this image?"
                onConfirm={handleDeleteImage}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" danger>
                  Delete Image
                </Button>
              </Popconfirm>
            </div>
          )}
        </Form.Item>
        <Form.Item
          name="firstName"
          label="First Name"
          rules={[{ required: true, message: "First name is required" }]}
        >
          <Input placeholder="First Name" />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="Last Name"
          rules={[{ required: true, message: "Last name is required" }]}
        >
          <Input placeholder="Last Name" />
        </Form.Item>

        {/* <Form.Item
          label="Agency"
          name="agencyId"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (getFieldValue("type") === InfluencerType.agency && !value) {
                  return Promise.reject(
                    new Error("Agency is required for Agency type")
                  );
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <Select
            showSearch
            searchValue={searchAgency}
            onSearch={setSearchAgency}
            placeholder="Search Agency"
            value={selectedAgency?.value || undefined} // Ensure value is a string or undefined
            onChange={(value) => {
              handleSelectAgency(value);
            }}
            filterOption={(input, option) => true}
            notFoundContent={
              isFetchingAgencies ? <CircularProgress /> : "No data available"
            }
          >
            {selectedAgency && (
              <Select.Option
                key={selectedAgency.value}
                value={selectedAgency.value}
              >
                {selectedAgency.label}
              </Select.Option>
            )}

            {(agencies?.data || []).length > 0 ? (
              (agencies?.data || [])
                .filter((item) => item._id !== selectedAgency?.value)
                .map((location) => (
                  <Select.Option key={location._id} value={location._id}>
                    {location.name}
                  </Select.Option>
                ))
            ) : (
              <Select.Option disabled key="no-data" value="no-data">
                No data available
              </Select.Option>
            )}
          </Select>
        </Form.Item> */}

        <Form.Item label="Location" name={"locationId"}>
          <Select
            showSearch
            searchValue={searchLocation}
            onSearch={setSearchLocation}
            placeholder="Search Location"
            value={selectedLocation?.value || undefined} // Ensure value is a string or undefined
            onChange={(value) => {
              handleSelectLocation(value);
            }}
            filterOption={(input, option) => true}
            notFoundContent={
              isFetching ? <CircularProgress /> : "No data available "
            }
          >
            {/* Show selected option at the top */}
            {selectedLocation && (
              <Select.Option
                key={selectedLocation.value}
                value={selectedLocation.value}
              >
                {selectedLocation.label}
              </Select.Option>
            )}

            {/* Render location options */}
            {(locationFacets || []).length > 0 ? (
              (locationFacets || [])
                .filter((item) => item.locationId !== selectedLocation?.value)
                .map((location) => (
                  <Select.Option
                    key={location.locationId}
                    value={location.locationId}
                  >
                    {location.description}
                  </Select.Option>
                ))
            ) : (
              <Select.Option disabled key="no-data" value="no-data">
                No data available
              </Select.Option>
            )}
          </Select>
        </Form.Item>

        <Form.Item
          name="gender"
          label="Gender"
          rules={[{ required: true, message: "Gender is Required" }]}
        >
          <Select>
            <Option value={GenderEnum.male}>{GenderEnum.male}</Option>
            <Option value={GenderEnum.female}>{GenderEnum.female}</Option>
            <Option value={GenderEnum.others}>{GenderEnum.others}</Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: "Category is required" }]}
        >
          <Select
            open={dropdownOpen}
            onDropdownVisibleChange={setDropdownOpen}
            showSearch
            mode="multiple"
            placeholder="Select or create a category"
            optionFilterProp="children"
            allowClear
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space
                  style={{
                    padding: "0 8px 4px",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Input
                    placeholder="Enter new category"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newRole) {
                        e.stopPropagation();

                        e.preventDefault();
                        addNewCategories(newRole);
                      }
                    }}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    disabled={!newRole}
                    onClick={() => addNewCategories(newRole)}
                  >
                    Add Category
                  </Button>
                </Space>
              </>
            )}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {availableCategories.map((role) => (
              <Option key={role} value={role}>
                {role}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      <div className={styles.footer}>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting || isUploading}
            loading={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default InfluencerUpdateForm;
